import { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'

import { requestProducts } from '@/actions/operations/product'

import {
  getCurrentUser,
  getOperationsProducts,
  getOperationsProductsCount,
  getOperationsLoading
} from '@/reducers/selectors'

import { Flex, Box, Table, Slot, Column, Row, Text, Loader } from '@/primitives'

import { Pagination } from '@/elements'

import HeaderV2 from '@/components/Operations/Shared/HeaderV2'
import TableFilter from './TableFilter'
import TableRow from './TableRow'

import history from '../../../../../history'

import {
  hasEditPermissions,
  RESOURCE_TYPE_OPERATIONS,
  RESOURCE_TYPE_OPERATIONS_SALES
} from '@/Util/PermissionUtils'

import Strings from '../Strings'

function ProductTable({ state, dispatchState, modulePath }) {
  const dispatch = useDispatch()

  const currentUser = getCurrentUser()
  const items = getOperationsProducts()
  const totalItems = getOperationsProductsCount()
  const loading = getOperationsLoading()

  const strings = Strings()

  const [totalPages, setTotalPages] = useState(1)
  const [pageSize, setPageSize] = useState(9)
  const [tableState, setTableState] = useState(state)
  const [canEdit, setCanEdit] = useState(false)

  useEffect(() => {
    if (currentUser.userName) {
      setCanEdit(
        hasEditPermissions(
          currentUser,
          RESOURCE_TYPE_OPERATIONS,
          RESOURCE_TYPE_OPERATIONS_SALES
        )
      )
    }
  }, [currentUser])

  useEffect(() => {
    if (tableState.page !== state.page || items.length === 0) {
      setTableState({ ...state })
      fetchProducts(state.page)
    }
  }, [state.page])

  useEffect(() => {
    if (
      tableState.organizations !== state.organizations ||
      tableState.produces !== state.produces ||
      tableState.varieties !== state.varieties ||
      tableState.statuses !== state.statuses
    ) {
      setTableState({ ...state })
      fetchProducts(1)
      changePage(1)
    }
  }, [state.organizations, state.produces, state.varieties, state.statuses])

  const fetchProducts = page => {
    if (
      state.organizations.length > 0 &&
      state.produces.length > 0 &&
      state.varieties.length > 0 &&
      state.statuses.length > 0
    ) {
      dispatch(
        requestProducts({
          filter: {
            organizationId: state.organizations,
            produceId: state.produces,
            varietyId: state.varieties,
            status: state.statuses
          },
          limit: pageSize,
          offset: page - 1
        })
      )
    }
  }

  const changePage = newPage => {
    dispatchState({
      page: newPage
    })
  }

  const onCreate = () => {
    history.push(`${modulePath}/create`)
  }

  const onEdit = id => {
    history.push(`${modulePath}/update/${id}`)
  }

  return (
    <Flex axisGap={400} direction='column'>
      <HeaderV2
        title={strings.manageProducts}
        buttonIcon={'add'}
        buttonText={strings.createProduct}
        buttonCallback={onCreate}
        showButton={canEdit}
      >
        <TableFilter state={state} dispatchState={dispatchState} />
      </HeaderV2>
      <Loader isLoading={loading}>
        {totalItems === 0 && (
          <Flex className={'Operations__Table__Empty'}>
            <Text>{strings.noProducts}</Text>
          </Flex>
        )}
        {totalItems > 0 && (
          <Table className='Operations__Table' aria-colcount='4'>
            <Slot name='head'>
              <Row>
                <Column>{strings.product}</Column>
                <Column>{strings.produce}</Column>
                <Column>{strings.weight}</Column>
                <Column>{strings.status}</Column>
                <Column />
              </Row>
            </Slot>
            <Slot name='body'>
              {items?.map(item => (
                <TableRow
                  key={item.id}
                  onEdit={onEdit}
                  canEdit={canEdit}
                  item={item}
                />
              ))}
            </Slot>
          </Table>
        )}
        {totalItems > pageSize && (
          <Box className='pagination-menu'>
            <Pagination
              type='simple'
              totalItems={totalItems}
              totalPages={totalPages}
              setTotalPages={setTotalPages}
              page={tableState.page}
              setPage={changePage}
              pageSize={pageSize}
              setPageSize={setPageSize}
            />
          </Box>
        )}
      </Loader>
    </Flex>
  )
}

export default ProductTable
